import React, { useState } from 'react';
import { useToolsContext } from '../../context/ToolsContext';
import './Tool.css';

/**
 * Tool:herramienta individual dentro de la barra de herramientas.
 * Cada herramienta puede tener un estado de activación, un sidebar y una acción al hacer clic.
 * 
 * @author Eva Rabasco
 * @param {string} id - El identificador único de la herramienta.
 * @param {string} icon - El nombre de la clase de ícono para la herramienta, que se usa en un `<i>` de Bootstrap Icons.
 * @param {string|null} [type=null] - El tipo de la herramienta (opcional). Se puede usar para diferenciar herramientas de diferentes comportamientos (ej. 'clickable').
 * @param {string} label - El texto que describe la herramienta. Se usa para accesibilidad.
 * @param {boolean} hasSidebar - Si la herramienta tiene un sidebar asociado que puede ser abierto o cerrado.
 * @param {string} sidebarTitle - El título que se muestra en el encabezado del sidebar.
 * @param {React.ReactNode} sidebarContent - El contenido que se muestra dentro del sidebar.
 */
const Tool = ({ id, icon, iconNode, type = null, label, hasSidebar, hasMoreTools = false, lockActive = false, keepActiveOnClose = false, sidebarTitle, sidebarContent, sidebarMoreTools = null }) => {
    const { toolStates, setToolState } = useToolsContext();
    // Obtiene el estado actual de la herramienta, o un estado por defecto si no existe
    const state = toolStates[id] || { active: false, sidebarOpen: false, type };

    const [position, setPosition] = useState({ top: undefined, left: undefined }); // Para almacenar la posición del botón

    /**
     * handleClick alterna el estado de la herramienta: activa/desactiva la herramienta y abre/cierra el sidebar si existe.
     */
    const handleClick = (e) => {

        // Calculamos la posición del botón para el modo hasMoreTools
        const buttonRect = e.target.getBoundingClientRect();
        setPosition({
            top: buttonRect.top + (window.scrollY - 4),
            left: buttonRect.right + (window.scrollX + 11)
        });

        // Alterna el estado del botón actual.
        // - Si lockActive=true, la herramienta se mantiene activa (solo abre/cierra el sidebar).
        // - keepActiveOnClose queda por compatibilidad, pero lockActive manda.
        const shouldLock = Boolean(lockActive);
        const nextActive = shouldLock ? true : ((keepActiveOnClose && state.active) ? true : !state.active);

        const newState = {
            ...state,
            active: nextActive,
            type: state.type
        };

        // Si la herramienta tiene un sidebar, alterna su estado abierto/cerrado
        if (hasSidebar || hasMoreTools) {
            newState.sidebarOpen = !state.sidebarOpen;
        }
        setToolState(id, newState); // Actualiza el estado de la herramienta en el contexto
    };

    /**
     * handleCloseSidebar cierra el sidebar de la herramienta y la desactiva.
     */
    const handleCloseSidebar = () => {

        // Cierra el sidebar y desactiva la herramienta
        const newState = {
            ...state,
            active: lockActive ? true : (keepActiveOnClose ? state.active : false),
            sidebarOpen: false
        };
        console.log('el id es: ', id + 'newstate ' + newState)
        setToolState(id, newState); // Actualiza el estado de la herramienta en el contexto
    };

    /**
     * handleCloseSidebarTools cierra el sidebarTools.
     */
    const handleCloseSidebarTools = () => {
        // Cierra el sidebar y desactiva la herramienta
        const newState = {
            ...state,
            active: lockActive ? true : (keepActiveOnClose ? state.active : false),
            sidebarOpen: false
        };
        setToolState(id, newState); // Actualiza el estado de la herramienta en el contexto
    };

    return (
        <div className='button-tool'>
            <span
                type="button"
                className={`list-item ${state.active ? 'active' : ''} ${hasSidebar || hasMoreTools ? 'has-sidebar' : ''}`}
                onClick={handleClick}
                aria-label={label}
                title={label}
            >
                {iconNode ? iconNode : <i className={`bi ${icon}`}></i>}
            </span>

            {/* Si la herramienta tiene un sidebar y está abierto, se renderiza el sidebar */}
            {/* {(hasSidebar || hasMoreTools) && state.sidebarOpen && ( */}
            {(hasSidebar) && state.sidebarOpen && (
                <div
                    className={`eol offcanvas ${state.sidebarOpen ? 'show' : ''}`}
                    data-bs-config='{"backdrop": false}'
                    tabIndex="-1"
                >
                    <div className="eol offcanvas-header">
                        <h5 className="eol offcanvas-title">{sidebarTitle}</h5> {/* Título del sidebar */}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleCloseSidebar} // Llama a handleCloseSidebar para cerrar el sidebar
                            aria-label="Cerrar" // Mejora la accesibilidad añadiendo un label de cierre
                        />
                    </div>
                    <div className="eol offcanvas-body">
                        {sidebarContent} {/* Renderiza el contenido dentro del sidebar */}
                    </div>
                </div>
            )}

            {/* {(hasMoreTools) && state.sidebarOpen && ( */}
            {(hasMoreTools) && (
                <div
                    className={`eol offcanvas more-tools ${state.sidebarOpen ? 'show' : ''} `}
                    id={`eol-offcanvas-${id}`}
                    tabIndex="-1"
                    data-bs-config='{"backdrop": false}'>
                    <div className="eol offcanvas-header">
                        <h5 className="eol offcanvas-title">{sidebarTitle}</h5> {/* Título del sidebar */}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleCloseSidebarTools} // Llama a handleCloseSidebar para cerrar el sidebar
                            aria-label="Cerrar" // Mejora la accesibilidad añadiendo un label de cierre
                        />
                    </div>
                    <div className="eol offcanvas-body">
                        {sidebarMoreTools} {/* Renderiza el contenido dentro del sidebar */}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tool;
